import type { MatchResult, MatchSpan } from "./match";
import type { Answer, Request } from "./search";

// What the latest search came to: what it found, or the sentence saying why nothing it found is on screen.
export type Outcome = { kind: "found"; result: MatchResult } | { kind: "stopped"; reason: string };

// A search over anything somebody pastes takes milliseconds, so one still going after this long is one that
// backtracks; what the wait costs is only how long the last highlight stands before the page says so.
export const SEARCH_BUDGET = 1000;

export const BACKTRACKED =
  "The search was stopped because this pattern backtracks too much on this text: it had not finished after a second.";
export const TOO_DEEP =
  "The search was stopped because this pattern backtracks too deeply on this text for the browser to follow.";
export const COULD_NOT_SEARCH = "The search could not be started.";

// The page's hold on the thread a search runs on: one request in flight, a budget on it once it begins, and a fresh
// worker in place of one that ran out of it.
export class SearchThread {
  private worker: Worker;
  private latest = 0;
  private pending = false;
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(private answer: (outcome: Outcome) => void) {
    this.worker = this.make();
  }

  search(request: Omit<Request, "id">) {
    // A search still running is one nobody is waiting for any more, and it holds the thread the next one needs.
    if (this.pending) this.replace();
    this.pending = true;
    this.worker.postMessage({ ...request, id: ++this.latest } satisfies Request);
  }

  close() {
    clearTimeout(this.timer);
    this.worker.terminate();
  }

  private make(): Worker {
    // Spelled statically, which is the whole of what lets the bundler find the worker and emit it as its own chunk.
    const worker = new Worker(new URL("/assets/search-q34n4tez.js", import.meta.url), { type: "module" });

    worker.onmessage = ({ data }: MessageEvent<Answer>) => {
      // An answer to anything but the latest request is about a pattern or a text that is no longer on screen.
      if (data.id !== this.latest) return;
      if (data.kind === "began") this.timer = setTimeout(() => this.stop(BACKTRACKED), SEARCH_BUDGET);
      else if (data.kind === "threw") this.settle({ kind: "stopped", reason: TOO_DEEP });
      else this.settle({ kind: "found", result: data.result });
    };

    // A worker that could not be built answers nothing, so the page is told rather than left showing the last answer.
    worker.onerror = () => this.settle({ kind: "stopped", reason: COULD_NOT_SEARCH });

    return worker;
  }

  private stop(reason: string) {
    this.replace();
    this.settle({ kind: "stopped", reason });
  }

  private settle(outcome: Outcome) {
    clearTimeout(this.timer);
    this.pending = false;
    this.answer(outcome);
  }

  // Terminating is the only way to end a search that is running, and the next one is given a worker at once rather
  // than paying to start one when it is asked for.
  private replace() {
    clearTimeout(this.timer);
    this.worker.terminate();
    this.worker = this.make();
  }
}

// What the highlight is drawn from: what the latest search found, and nothing while there is no answer or it stopped.
export function matchesOf(outcome: Outcome | null): MatchSpan[] {
  return outcome?.kind === "found" ? outcome.result.matches : NOTHING;
}

// One array for every time there is nothing, so an effect keyed on the matches does not fire for a new empty one.
const NOTHING: MatchSpan[] = [];
